// React Dependencies
import React, { useRef, useState, useReducer, useMemo } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Button, IconButton, Dialog, DialogTitle, DialogContent, TextField, Typography,
  DialogActions, Tooltip, Select, MenuItem, RadioGroup, FormControlLabel, Radio,
} from '@material-ui/core';
import { Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';

// Internal Dependencies
import OverLoading from '../../global/OverLoading';
import apiService from '../../../services.js/apiService';
import Colours from '../../../styles/colours';
import { updateModalContent as updateModalAction, toggleModal as toggleModalAction } from '../../../actions/modal';
import { Status, TrailType, Url } from '../../../common/constants';

const defaultCreationModalState = { open: false, name: '', type: '' };

const Trails = ({ match, updateModalContent, toggleModal }) => {
  const pageId = match.params.id;

  const tableRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [creationModal, setCreationModal] = useReducer((state, action) => ({ ...state, ...action }), defaultCreationModalState);

  const fetchTableData = (query) => async (resolve) => {
    try {
      setLoading(true);
      query.pageNo = query.page;
      const { page } = query;
      const queryFilters = Object.fromEntries(query.filters.map((filters) => [filters.column.field, filters.value]));
      const result = await apiService.getTrails(pageId, { ...query, ...queryFilters });

      resolve({ data: result.rows, page, totalCount: result.totalCount });
    } catch (err) {
      toast.error(err.message);
      resolve({ data: [], page: 0, totalCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  const submitTrail = async () => {
    try {
      setLoading(true);
      await apiService.createTrail(pageId, { name: creationModal.name, type: creationModal.type });
      setCreationModal(defaultCreationModalState);
      tableRef.current.onQueryChange(); // updates table data
      toast.success('Trail created successfully.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSpecificPath = (trailId) => Url.Trail.replace(':pageId', pageId).replace(':trailId', trailId);

  const handleDeleteModal = (trailId) => {
    updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this trail will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            setLoading(true);
            await apiService.deleteTrail(pageId, trailId);
            tableRef.current.onQueryChange();
            toast.success('Successfully deleted trail.');
          } catch (err) {
            toast.error(err.message);
          } finally {
            setLoading(false);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });
    toggleModal();
  };

  const columns = useMemo(() => [
    {
      title:     'ID',
      filtering: true,
      editable:  'never',
      field:     'id',
      cellStyle: { width: '15%' },
      render:    (rowData) => (
        <Link target="_blank" to={getSpecificPath(rowData.id)}>
          {rowData.id}
        </Link>
      ),
    },
    {
      title:     'Name',
      filtering: true,
      editable:  'never',
      field:     'name',
      cellStyle: { width: '40%' },
      render:    (rowData) => (
        <Link target="_blank" to={getSpecificPath(rowData.id)}>
          {rowData.name}
        </Link>
      ),
    },
    {
      title:           'Status',
      filtering:       true,
      editable:        'never',
      field:           'status',
      cellStyle:       { width: '15%' },
      filterComponent: (props) => (
        <Select
          className="w-100"
          // eslint-disable-next-line react/prop-types
          onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
        >
          <MenuItem value="">-</MenuItem>
          <MenuItem value={Status.Draft}>{Status.Draft}</MenuItem>
          <MenuItem value={Status.Live}>{Status.Live}</MenuItem>
          <MenuItem value={Status.Deactivated}>{Status.Deactivated}</MenuItem>
        </Select>
      ),
    },
    {
      title:           'Type',
      filtering:       true,
      editable:        'never',
      field:           'type',
      cellStyle:       { width: '15%' },
      filterComponent: (props) => (
        <Select
          className="w-100"
          // eslint-disable-next-line react/prop-types
          onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
        >
          <MenuItem value="">-</MenuItem>
          <MenuItem value={TrailType.Food}>{TrailType.Food}</MenuItem>
          <MenuItem value={TrailType.Hawker}>{TrailType.Hawker}</MenuItem>
        </Select>
      ),
    },
    {
      title:     'Actions',
      filtering: false,
      editable:  'never',
      cellStyle: { width: '15%' },
      render:    (rowData) => (
        <div className="w-100 d-flex" style={{ gap: '0.8rem' }}>
          <Tooltip title="Edit">
            <IconButton size="medium" onClick={() => window.open(getSpecificPath(rowData.id), '_blank')}>
              <EditIcon htmlColor={Colours.Black} />
            </IconButton>
          </Tooltip>
          {rowData.status === Status.Draft && (
            <Tooltip title="Delete">
              <IconButton size="medium" onClick={() => handleDeleteModal(rowData.id)}>
                <DeleteIcon htmlColor={Colours.Black} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ], []);

  return (
    <>
      {loading && <OverLoading />}

      <div className="row" style={{ backgroundColor: '#fff', marginLeft: 1 }}>
        <div className="col-12 d-flex align-items-center" style={{ padding: '5px 15px' }}>
          <Typography className="mr-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Trails</Typography>
          <Button
            onClick={() => setCreationModal({ open: true })}
            variant="contained"
            color="primary"
            size="medium"
          >
            + Add
          </Button>
        </div>
        <div className="col-12 card-left">
          <MaterialTable
            tableRef={tableRef}
            columns={columns}
            isLoading={loading}
            components={{ Toolbar: () => null }}
            options={{ filtering: true, search: false, debounceInterval: 500, emptyRowsWhenPaging: false }}
            data={(query) => new Promise(fetchTableData(query))}
          />
        </div>
      </div>

      <Dialog open={creationModal.open} onClose={() => setCreationModal(defaultCreationModalState)} fullWidth>
        <DialogTitle className="px-4 pt-4">
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Add Trail
          </Typography>
        </DialogTitle>
        <DialogContent className="px-4 pb-4">
          <div className="mb-4">
            <Typography variant="subtitle1">Name</Typography>
            <TextField
              variant="outlined"
              className="w-100"
              onChange={({ target }) => setCreationModal({ name: target.value })}
              value={creationModal.name}
            />
          </div>

          <div>
            <Typography variant="subtitle1">
              Type
            </Typography>
            <RadioGroup
              row
              name="type"
              value={creationModal.type}
              onChange={({ target }) => setCreationModal({ type: target.value })}
            >
              {Object.values(TrailType).map((type) => (
                <div key={type} className="mr-3">
                  <FormControlLabel
                    value={type}
                    control={<Radio color="secondary" />}
                    label={type}
                  />
                </div>
              ))}
            </RadioGroup>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreationModal({ open: false })} color="primary">Cancel</Button>
          <Button onClick={submitTrail} color="primary">Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

Trails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    url: PropTypes.string.isRequired,
  }).isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(null, { toggleModal: toggleModalAction, updateModalContent: updateModalAction })(Trails);
